import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import {
  ShieldCheck, Loader2, ArrowRight, ArrowLeft, ShieldAlert, User, Lock, Users,
  Activity, MessageCircle, BookOpen, GraduationCap, Hand, Music, Brain, HeartHandshake,
  Move, Apple, Syringe, HeartPulse, Stethoscope, ClipboardList, Building2,
} from "lucide-react";

const TEAL = "#0f766e";

const ROLES = [
  { icon: Activity, fr: "Kinésithérapeute", nl: "Kinesitherapeut", en: "Physiotherapist", photo: "/img/photos/27f71b045d52.jpg" },
  { icon: MessageCircle, fr: "Logopède", nl: "Logopedist", en: "Speech therapist", photo: "/img/photos/c3ff5adc363d.jpg" },
  { icon: BookOpen, fr: "Éducateur", nl: "Opvoeder", en: "Educator", photo: "/img/photos/db1a44d6c7b9.jpg" },
  { icon: GraduationCap, fr: "Éducateur spécialisé", nl: "Gespecialiseerd opvoeder", en: "Special educator", photo: "/img/photos/c07c3e9ee1e9.jpg" },
  { icon: Hand, fr: "Ergothérapeute", nl: "Ergotherapeut", en: "Occupational therapist", photo: "/img/photos/3aa9cdf00268.jpg" },
  { icon: Music, fr: "Animateur", nl: "Animator", en: "Activity leader", photo: "/img/photos/7e257a553d84.jpg" },
  { icon: Brain, fr: "Référent démence", nl: "Referentiepersoon dementie", en: "Dementia referent", photo: "/img/photos/97aeaf8ec621.jpg" },
  { icon: HeartHandshake, fr: "Psychologue", nl: "Psycholoog", en: "Psychologist", photo: "/img/photos/e4c5932aca22.jpg" },
  { icon: Move, fr: "Psychomotricien", nl: "Psychomotorisch therapeut", en: "Psychomotor therapist", photo: "/img/photos/f71e0a14c0d6.jpg" },
  { icon: Apple, fr: "Diététicien", nl: "Diëtist", en: "Dietitian", photo: "/img/photos/1ebc0717ffdd.jpg" },
  { icon: Syringe, fr: "Infirmier", nl: "Verpleegkundige", en: "Nurse", photo: "/img/photos/6f08d85d386c.jpg" },
  { icon: HeartPulse, fr: "Aide-soignant", nl: "Zorgkundige", en: "Care assistant", photo: "/img/photos/02cea3b8f676.jpg" },
  { icon: Users, fr: "Assistant social", nl: "Maatschappelijk assistent", en: "Social worker", photo: "/img/photos/facf1ffbf551.jpg" },
  { icon: Stethoscope, fr: "Médecin", nl: "Arts", en: "Doctor", photo: "/img/photos/9701f7bdf04c.jpg" },
  { icon: ClipboardList, fr: "Responsable d'unité", nl: "Afdelingsverantwoordelijke", en: "Unit manager", photo: "/img/photos/c07c3e9ee1e9.jpg" },
  { icon: Building2, fr: "Direction", nl: "Directie", en: "Management", photo: "/img/photos/7e257a553d84.jpg" },
];

const RoleCard = ({ role, lang }) => {
  const Icon = role.icon;
  const label = role[lang] || role.fr;
  return (
    <div className="relative rounded-2xl overflow-hidden shadow-lg ring-1 ring-white/10 group">
      <img src={role.photo} alt={label} loading="lazy"
        className="h-36 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(6,45,42,0) 30%, rgba(6,45,42,0.85) 100%)" }} />
      <div className="absolute top-2.5 left-2.5 h-8 w-8 rounded-xl flex items-center justify-center backdrop-blur-md bg-white/15 ring-1 ring-white/25">
        <Icon className="h-4 w-4 text-white" />
      </div>
      <div className="absolute bottom-2.5 left-3 right-3">
        <div className="text-white font-semibold text-sm leading-tight drop-shadow">{label}</div>
      </div>
    </div>
  );
};

const MarqueeColumn = ({ roles, direction, lang, duration }) => {
  const items = [...roles, ...roles];
  return (
    <div className="relative h-full overflow-hidden">
      <div className="flex flex-col gap-4" style={{ animation: `${direction} ${duration}s linear infinite` }}>
        {items.map((role, i) => (
          <RoleCard key={i} role={role} lang={lang} />
        ))}
      </div>
    </div>
  );
};

export default function Login() {
  const { login } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const colA = ROLES.filter((_, i) => i % 2 === 0);
  const colB = ROLES.filter((_, i) => i % 2 === 1);

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden lg:grid lg:grid-cols-[1.05fr_0.95fr] relative" data-testid="login-page">
      <style>{`
        @keyframes marqueeUp { from { transform: translateY(0); } to { transform: translateY(-50%); } }
        @keyframes marqueeDown { from { transform: translateY(-50%); } to { transform: translateY(0); } }
      `}</style>

      {/* Sélecteur de langue */}
      <div className="absolute top-5 right-5 z-30"><LanguageSwitcher variant="onlight" /></div>

      {/* ══ Panneau gauche : marque + défilé des métiers (desktop) ══ */}
      <div className="relative hidden lg:flex flex-col overflow-hidden text-white lg:h-screen"
        style={{ background: "linear-gradient(150deg, #0b3d3a 0%, #0f766e 55%, #0d9488 100%)" }}>
        <Link to="/decouvrir" data-testid="login-back-vitrine"
          className="absolute top-5 left-6 z-20 inline-flex items-center gap-1.5 text-sm font-medium text-white/80 hover:text-white transition">
          <ArrowLeft className="h-4 w-4" /> {t("login.backToVitrine")}
        </Link>

        <div className="relative z-10 px-10 pt-20 pb-6 shrink-0">
          <div className="flex items-center gap-3">
            <img src="/carepilot-64.png" alt="CarePilot" className="h-12 w-12 rounded-2xl shadow-xl" />
            <div className="leading-tight">
              <span className="font-display text-2xl font-extrabold tracking-tight">CarePilot</span>
              {t("login.brandSub") && <div className="text-[10px] font-semibold tracking-[0.22em] uppercase mt-0.5 text-white/60">{t("login.brandSub")}</div>}
            </div>
          </div>
          <h2 className="font-display text-3xl xl:text-4xl font-extrabold tracking-tight mt-8 max-w-md">
            {t("login.heroTitle")}
          </h2>
          <p className="text-white/70 text-sm mt-3 max-w-md">{t("login.heroSubtitle")}</p>
        </div>

        {/* Colonnes défilantes */}
        <div className="relative flex-1 min-h-[420px] px-8 pb-8">
          <div className="grid grid-cols-2 gap-4 h-full">
            <MarqueeColumn roles={colA} direction="marqueeUp" duration={38} lang={lang} />
            <MarqueeColumn roles={colB} direction="marqueeDown" duration={44} lang={lang} />
          </div>
          {/* Dégradés de fondu haut/bas */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-16" style={{ background: "linear-gradient(180deg, #0f766e, transparent)" }} />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16" style={{ background: "linear-gradient(0deg, #0d9488, transparent)" }} />
        </div>
      </div>

      {/* ══ Panneau droit : formulaire de connexion ══ */}
      <div className="flex items-center justify-center px-4 py-12 relative lg:h-screen lg:overflow-y-auto"
        style={{ background: "radial-gradient(1100px 600px at 50% -10%, #e6f5f3 0%, transparent 60%), #f5f7f9" }}>
        {/* Retour accueil (mobile uniquement, panneau gauche caché) */}
        <Link to="/decouvrir"
          className="lg:hidden absolute top-5 left-5 z-10 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition">
          <ArrowLeft className="h-4 w-4" /> {t("login.backToVitrine")}
        </Link>

        <motion.div initial={{ opacity: 0, y: 22, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: [0.2, 0.7, 0.2, 1] }} className="w-full max-w-md">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-8 lg:hidden">
              <img src="/carepilot-64.png" alt="CarePilot" className="h-12 w-12 rounded-2xl"
                style={{ boxShadow: "0 8px 24px -10px rgba(15,118,110,0.7)" }} />
              <div className="leading-tight">
                <span className="font-display text-2xl font-extrabold tracking-tight text-slate-900">CarePilot</span>
                {t("login.brandSub") && <div className="text-[10px] font-semibold tracking-[0.22em] uppercase mt-0.5 text-slate-400">{t("login.brandSub")}</div>}
              </div>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold mb-4"
              style={{ background: "rgba(15,118,110,0.08)", color: TEAL }}>
              <ShieldCheck className="h-3.5 w-3.5" /> {t("login.secure")}
            </div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{t("login.submit")}</h1>
            <p className="text-sm text-slate-500 mt-1.5">{t("login.secure")}</p>

            <form onSubmit={submit} className="mt-7 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-700">{t("login.usernameLabel")}</Label>
                <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 h-12 focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-500/25 transition">
                  <User className="h-4 w-4 text-slate-400 shrink-0" />
                  <Input id="username" data-testid="login-username" value={username}
                    onChange={(e) => setUsername(e.target.value.toUpperCase())} placeholder={t("login.usernamePlaceholder")}
                    className="h-11 border-0 bg-transparent px-0 font-mono tracking-widest uppercase text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0"
                    autoCapitalize="characters" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">{t("login.passwordLabel")}</Label>
                <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 h-12 focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-500/25 transition">
                  <Lock className="h-4 w-4 text-slate-400 shrink-0" />
                  <Input id="password" type="password" data-testid="login-password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                    className="h-11 border-0 bg-transparent px-0 text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0" required />
                </div>
              </div>

              {error && (
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} data-testid="login-error"
                  className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {error}
                </motion.div>
              )}

              <Button type="submit" data-testid="login-submit" disabled={busy}
                className="w-full h-12 text-base font-semibold border-0 text-white rounded-xl shadow-md hover:shadow-lg transition-shadow"
                style={{ background: "linear-gradient(135deg, #0f766e, #0d9488 60%, #14b8a6)" }}>
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t("login.submit")} <ArrowRight className="ml-1 h-4 w-4" /></>}
              </Button>
            </form>

            <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-3.5" data-testid="login-gdpr">
              <div className="flex items-center gap-2 font-semibold text-sm text-slate-700">
                <ShieldAlert className="h-4 w-4" style={{ color: TEAL }} /> {t("login.gdprTitle")}
              </div>
            </div>

            <Link to="/decouvrir" data-testid="login-public-link"
              className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-700 hover:border-teal-500/60 hover:text-teal-700 transition">
              <Users className="h-4 w-4" style={{ color: TEAL }} /> {t("public.iAmFamily")} / {t("public.iAmPartner")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6" data-testid="login-copyright">© 2026 Xavier PEYOU</p>
        </motion.div>
      </div>
    </div>
  );
}
